import { useQuery } from '@tanstack/react-query';
import { MdArrowDropDown, MdArrowDropUp } from 'react-icons/md';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/Button';
import { Tag } from '@/components/Tag';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import type { AgentApiSharedJobPostsMetadata } from '@/types/serializers';
import { useState } from 'react';

export default function ProviderConnectionDropdown() {
  const [isOpen, setIsOpen] = useState(false);

  const { data: metadata } = useQuery<AgentApiSharedJobPostsMetadata>({
    queryKey: ['/agent/api/shared_job_posts/metadata'],
    queryFn: async () => {
      const response = await fetch('/agent/api/shared_job_posts/metadata.json');
      if (!response.ok) {
        throw new Error('Failed to fetch metadata');
      }
      return response.json();
    },
  });

  const permittedProviders = metadata?.permitted_providers ?? [];
  const availableProviders = [...(metadata?.available_providers ?? [])].sort((a, b) => {
    const aConnected = permittedProviders.includes(a.key);
    const bConnected = permittedProviders.includes(b.key);
    if (aConnected === bConnected) return 0;
    return aConnected ? -1 : 1;
  });
  const connectedCount = permittedProviders.length;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button color="secondary" size="md" active={isOpen}>
          {connectedCount}件のアカウントで連携中
          {isOpen ? <MdArrowDropUp className="size-5" /> : <MdArrowDropDown className="size-5" />}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[300px] rounded p-0">
        <div className="flex flex-col">
          {availableProviders.map((provider) => {
            const isConnected = permittedProviders.includes(provider.key);
            return (
              <Link
                key={provider.key}
                to="/agent/linked_accounts/shared"
                className="flex items-center gap-2 px-3 py-2 no-underline hover:bg-gray-50"
              >
                <Tag
                  color={isConnected ? 'blue' : 'gray'}
                  size="sm"
                  className="w-16 justify-center"
                >
                  {isConnected ? '連携済み' : '未連携'}
                </Tag>
                <span className="text-sm text-main">{provider.label}</span>
              </Link>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
